import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const { switchLanguage, language } = useLanguage();
  const { t } = useTranslation();

  const isActive = (path) => location.pathname === path;

  const navigation = [
    { 
      name: t('header.products'), 
      href: '/proxies',
      dropdown: [
        { name: t('header.dropdown.products.residentialProxies'), href: '/proxies/residential' },
        { name: t('header.dropdown.products.mobileProxies'), href: '/proxies/mobile' }
      ]
    },
    { 
      name: t('header.useCases'), 
      href: '/use-cases',
      dropdown: [
        { name: t('header.dropdown.useCases.proxiesForAI'), href: '/use-cases/proxies-for-ai' },
        { name: t('header.dropdown.useCases.webScraping'), href: '/use-cases/web-scraping' },
        { name: t('header.dropdown.useCases.websiteTesting'), href: '/use-cases/website-testing' },
        { name: t('header.dropdown.useCases.travelFareAggregation'), href: '/use-cases/travel-fare-aggregation' },
        { name: t('header.dropdown.useCases.marketResearch'), href: '/use-cases/market-research' },
        { name: t('header.dropdown.useCases.googleProxies'), href: '/use-cases/google-proxies' },
        { name: t('header.dropdown.useCases.accessRestrictedContent'), href: '/use-cases/access-restricted-content' },
        { name: t('header.dropdown.useCases.privacyAdvantages'), href: '/use-cases/privacy-advantages' }
      ]
    },
    { 
      name: t('header.pricing'), 
      href: '/pricing',
      dropdown: [
        { name: t('header.dropdown.pricing.residentialProxies'), href: '/pricing/residential' },
        { name: t('header.dropdown.pricing.mobileProxies'), href: '/pricing/mobile' }
      ]
    },
    { name: t('header.docs'), href: '/docs' },
    ...(language === 'en' ? [{ name: t('header.blog'), href: '/blog' }] : []),
    { name: t('header.about'), href: '/about' }
  ];

  return (
    <header className="bg-slate-950/90 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/logo-white.png" 
              alt="IP9" 
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                <Link
                  to={item.href}
                  className={`text-sm font-medium transition-colors duration-200 flex items-center space-x-1 ${
                    isActive(item.href)
                      ? 'text-cyan-400'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  <span>{item.name}</span>
                  {item.dropdown && <ChevronDown className="w-4 h-4" />}
                </Link>
                
                {/* Dropdown Menu */}
                {item.dropdown && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-slate-900 border border-slate-700 shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2">
                      {item.dropdown.map((dropItem) => (
                        <Link
                          key={dropItem.name}
                          to={dropItem.href}
                          className="block px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 border-l-2 border-transparent hover:border-cyan-500 transition-colors duration-200"
                        >
                          {dropItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={() => switchLanguage(language === 'en' ? 'zh' : 'en')}
              className="text-slate-300 hover:text-white text-sm font-medium transition-colors duration-200"
            >
              {t('header.chinese')}
            </button>
            <a 
              href="https://dashboard.ip9.io" 
              className="text-slate-300 hover:text-white hover:bg-slate-700 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {t('header.login')}
            </a>
            <a 
              href="https://dashboard.ip9.io" 
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-2 text-sm font-semibold transition-colors duration-200"
            >
              {t('header.getStarted')}
            </a>
          </div>

          {/* Mobile header buttons */}
          <div className="md:hidden flex items-center space-x-3">
            <a 
              href="https://dashboard.ip9.io"
              className="text-slate-300 hover:text-white text-sm font-medium"
            >
              {t('header.dashboard')}
            </a>
            <button
              className="text-slate-300 hover:text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute left-0 right-0 top-full bg-slate-800 border-t border-slate-700 z-50 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-4 pb-8 space-y-4 max-h-full">
            {navigation.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.href}
                  className={`block text-sm font-medium py-2 ${
                    isActive(item.href)
                      ? 'text-cyan-400'
                      : 'text-slate-300 hover:text-white'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
                {item.dropdown && (
                  <div className="ml-4 mt-2 space-y-2">
                    {item.dropdown.map((dropItem) => (
                      <Link
                        key={dropItem.name}
                        to={dropItem.href}
                        className="block text-sm text-slate-400 hover:text-white py-1"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {dropItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 border-t border-slate-700 space-y-2">
              <button
                onClick={() => {
                  switchLanguage(language === 'en' ? 'zh' : 'en');
                  setIsMenuOpen(false);
                }}
                className="w-full text-left text-slate-300 hover:text-white py-2 text-sm font-medium"
              >
                {t('header.chinese')}
              </button>
              <a 
                href="https://dashboard.ip9.io"
                className="block w-full"
              >
                <Button variant="ghost" className="w-full text-slate-300 hover:text-white hover:bg-slate-700 justify-start">
                  {t('header.login')}
                </Button>
              </a>
              <a 
                href="https://dashboard.ip9.io"
                className="block w-full"
              >
                <Button className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                  {t('header.getStarted')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;